import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { confirmDialog } from 'primereact/confirmdialog';

import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { paginationDropdownOptions } from 'constants/dropdownOptions';
import {
  deleteRejectedLeadIntakeService,
  getPendingLeadIntakeService,
  getLeadDetailsService,
} from 'services/intakeforms/leadIntakeService';
import { handlePageNumberInPagination } from 'common/pagination';
import { convertObjectToQuery } from 'utils/utils';
import { createLead } from 'services/lead/leadServices';
import { prepareBillingData } from '../helpers/helpers';
import Cookies from 'universal-cookie';
import { queryClient } from 'config/queryClient';
import { queryKeys } from 'modules/LeadsV2/config/queryClient';

const cookie = new Cookies();

export function useLeadIntake(_pageSize = 10, leadType = 'online') {
  const userContext = useUserDetailsContext();
  const { addToast } = useToast();

  const [data, setData] = useState([]);
  const [isForward, setIsForward] = useState(true);
  const [pageSize, setpageSize] = useState(userContext?.firmDetails?.pagination_preference?.settings?.lead_intake_list?.self || _pageSize);
  const [leadDetails, setLeadDetails] = useState({}); // Store additional lead details
  const [loadingDetails, setLoadingDetails] = useState({}); // Track loading state for each lead

  const next_Token = data?.next_token;
  const transition_Token = data?.transition_token;

  // Return different data fields based on lead type
  const leadForms = leadType === 'capturenow' ? data?.capturenow_lead || [] : data?.online_form || [];

  useEffect(() => {
    setpageSize(userContext?.firmDetails?.pagination_preference?.settings?.lead_intake_list?.self || _pageSize);
  }, [userContext]); // eslint-disable-line

  const hasNext = next_Token === null && isForward;
  const hasPrevious = transition_Token === null || (transition_Token && next_Token === null && !isForward);

  const paginatorTemplate = {
    layout: 'RowsPerPageDropdown PrevPageLink NextPageLink',
    RowsPerPageDropdown: () => {
      return (
        <React.Fragment>
          <span className="mx-1" style={{ color: 'var(--black-500)', userSelect: 'none' }}>
            Item Per Page
          </span>
          <Dropdown value={pageSize} options={paginationDropdownOptions} onChange={onPageChange} />
        </React.Fragment>
      );
    },
    NextPageLink: ({ iconClassName }) => {
      return (
        <Button onClick={fetchNext} disabled={hasNext} className={'p-paginator-next p-paginator-element p-link'} icon={iconClassName} />
      );
    },
    PrevPageLink: ({ iconClassName }) => {
      return (
        <Button onClick={fetchprev} disabled={hasPrevious} className={'p-paginator-next p-paginator-element p-link'} icon={iconClassName} />
      );
    },
  };

  const fetchNext = () => {
    setIsForward(true);
    loadList({
      limit: pageSize,
      pagination_token: !isForward ? transition_Token : next_Token,
      pagination_direction: 'forward',
    });
  };

  const fetchprev = () => {
    setIsForward(false);
    loadList({
      limit: pageSize,
      pagination_token: isForward ? transition_Token : next_Token,
      pagination_direction: 'reverse',
    });
  };

  const onPageChange = (e) => {
    setpageSize(e.value);
    handlePageNumberInPagination(userContext, 'settings', 'lead_intake_list', 'self', e.value);
    loadList({
      limit: e.value,
      pagination_token: '',
      pagination_direction: 'forward',
    });
    setIsForward(true);
  };

  const loadList = ({ limit = pageSize || 10, pagination_token = '', pagination_direction = 'forward' }) => {
    const qs = {
      pagination_token,
      pagination_direction,
      limit,
      lead_type: leadType, // Add lead_type to distinguish between online forms and capturenow
    };
    getPendingLeadIntakeService(convertObjectToQuery(qs))
      .then((response) => setData({ ...response?.data }))
      .catch(() =>
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.INTAKE_FORM_LIST_FAILED)
      );
  };

  // Function to fetch additional lead details
  const fetchLeadDetails = async (lead) => {
    const tenantId = cookie.get('tenantId');

    // Use different field names based on lead type
    let leadId;
    if (leadType === 'capturenow') {
      leadId = lead?.capturenow_lead_id || lead?.sk || lead?.id;
    } else {
      leadId = lead?.online_lead_id || lead?.sk || lead?.id;
    }

    if (!tenantId || !leadId) {
      console.error('Missing tenant ID or lead ID');
      return;
    }

    // Set loading state for this specific lead
    setLoadingDetails((prev) => ({ ...prev, [leadId]: true }));

    try {
      const response = await getLeadDetailsService(tenantId, leadId, leadType);

      // Store the response data for this lead
      setLeadDetails((prev) => ({
        ...prev,
        [leadId]: response?.data || response,
      }));
    } catch (error) {
      console.error('Error fetching lead details:', error);
      // Store error state for this lead
      setLeadDetails((prev) => ({
        ...prev,
        [leadId]: { error: 'Failed to fetch details' },
      }));
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [leadId]: false }));
    }
  };

  useEffect(() => {
    loadList({});
  }, [leadType]); // Reload when leadType changes

  // ----------------------------List Pending Intake Forms------------------------------------

  const rejectIntake = (data) => {
    confirmDialog({
      message: 'Are you sure you want to delete this lead?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        deleteRejectedLeadIntakeService({ sk: data?.sk, pk: data?.pk }).then(() => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.INTAKE_FORM_REJECTED);
          loadList({});
        });
      },
    });
  };

  const acceptIntake = async (data, callBack) => {
    const customFields = data?.form_data?.filter((v) => v?.res_type === 'custom-field');
    const pa = data?.form_data?.find((v) => v?.field_type === 'practice_area');
    const onlineLeadFields = data?.form_data?.filter((v) => v?.field_type === 'general' && v?.type !== 'paragraph');
    let contact = {
      ...data?.contact_data,
      custom_field: customFields,
      custom_field_toggle: customFields?.length > 0,
    };

    const formData = {
      is_draft: true,
      draft_payload: {
        case_description: {
          office_location: '',
          custom_field: [],
          case_matter: '',
          case_number: '',
          case_name: '',
          custom_field_toggle: false,
          case_practice_area: pa?.value || '',
          _practice_area_: pa?._practice_area_ || '',
        },
        lead_details: {
          description: data?.transcript_summary || '', // Use transcript_summary for CaptureNow leads
          tags: [],
          conflict_check: false,
          gratitude: null,
          gratitude_item: '',
        },
        contact,
        billing_preferences: prepareBillingData(data?.billing_preferences),
        referred_from: null,
        online_lead_fields: onlineLeadFields,
        lead_type: leadType, // Add lead_type to the lead data
      },
    };
    createLead(formData)
      .then(() => {
        deleteRejectedLeadIntakeService({ sk: data?.sk, pk: data?.pk }).then(() => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_CREATE_SUCCESS);
          loadList({});
          queryClient.invalidateQueries({ queryKey: queryKeys.leads.all });
          typeof callBack === 'function' && callBack();
        });
      })
      .catch((error) => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_CREATE_FAILURE);
        console.error(error);
      });
  };

  return {
    leadForms,
    paginatorTemplate,
    pageSize,
    rejectIntake,
    acceptIntake,
    fetchLeadDetails,
    leadDetails,
    loadingDetails,
  };
}
